import IconButton from '@deltek/specification-client/core/Buttons/IconButton'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { SvgIcon, Theme, makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import { ActionButtonVariant } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import React, { ComponentProps, FC, ReactElement, ReactNode, memo } from 'react'
import { ToolTipText } from './ToolTipText'

interface styleProps {
    iconOverlaySize?: number
}

export const useActionButtonStyles = makeStyles((theme: Theme) => ({
    iconOverlay: {
        opacity: '1',
        height: ({ iconOverlaySize }: styleProps) =>
            pxToRem(iconOverlaySize ?? 30),
        width: ({ iconOverlaySize }: styleProps) =>
            pxToRem(iconOverlaySize ?? 30),
        alignContent: 'center',
    },
    iconBGColor: {
        backgroundColor: theme.palette.background.default,
    },
    divider: {
        color: theme.palette.divider,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: pxToRem(10),
    },
    iconOverlayContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
}))

const svgIconDefaultProps: Partial<ComponentProps<typeof SvgIcon>> = {
    viewBox: '-1 -2 26 26',
    xmlSpace: 'preserve',
    fill: '#ffffff',
    width: '24',
    height: '24',
    style: {
        color: '#ffffff',
        cursor: 'pointer',
    },
}

export interface ActionButtonProps<TVariant> {
    children?: ReactNode
    className?: string
    tooltip?: {
        label: string
        smallLabel?: string
    }
    variant: TVariant | 'Divider'
    disabled?: boolean
    loading?: boolean
    onClick?: VoidFunction | ((...args: any[]) => void)
    svgIcon?: ReactElement
    isHidden?: boolean
    src?: string // TODO: remove this prop once all action buttons implementing <ActionButton/> component are SVG icons
    iconOverlaySize?: number
    hasMultipleIcons?: boolean
    enableTooltipOnDisabled?: boolean
}

export declare type ActionButtonVariantType = typeof ActionButtonVariant[keyof typeof ActionButtonVariant]
export declare type PickActionButtonVariant<
    T extends ActionButtonVariantType
> = keyof Pick<typeof ActionButtonVariant, T>

// use to define strongly type of ActionButton object that will use to render buttons in the ActionBar
// TODO: add 'Divider(count)' string literals as necessary for divider.
export declare type ActionButtons<
    TVariant extends ActionButtonVariantType = ActionButtonVariantType
> = Record<TVariant | 'Divider1' | 'Divider2', ActionButtonProps<TVariant>>

export const Separator = memo(() => {
    const classes = useActionButtonStyles({ iconOverlaySize: 30 })
    return <div className={classes.divider} />
})

export const ExtendedSvgIcon = memo<Partial<ComponentProps<typeof SvgIcon>>>(
    ({ children, ...overrideProps }) => {
        return (
            <SvgIcon {...svgIconDefaultProps} {...overrideProps}>
                {children}
            </SvgIcon>
        )
    }
)
export const ActionButton: FC<ActionButtonProps<
    ActionButtonVariantType
>> = React.memo(
    ({
        children,
        svgIcon,
        tooltip,
        variant,
        disabled,
        loading,
        onClick,
        isHidden,
        iconOverlaySize = 30,
        hasMultipleIcons = false,
        enableTooltipOnDisabled = false,
    }) => {
        const {
            iconOverlay,
            iconBGColor,
            iconOverlayContainer,
        } = useActionButtonStyles({
            iconOverlaySize: iconOverlaySize,
        })
        const disabledOrLoading = disabled || loading

        if (isHidden) {
            return null
        }

        // render button group divider
        if (variant === 'Divider') {
            if (svgIcon) {
                return svgIcon
            }
            return <Separator />
        }

        // render Action Buttons
        const iconButtonContent = (
            <IconButton
                data-testid={variant}
                disabled={disabledOrLoading}
                loading={loading}
                onClick={onClick}
                hasShadow={false}
                color="#ffffff"
                borderColor={iconBGColor}
                backgroundColor={iconBGColor}
                hoverColor="rgba(255, 255, 255, 0.2)"
                aria-label={`${tooltip?.label} button`}
            >
                <div
                    data-testid={`${variant}overlay`}
                    className={clsx(
                        iconOverlay,
                        hasMultipleIcons ? iconOverlayContainer : ''
                    )}
                    style={{ opacity: disabledOrLoading ? '0.45' : '1' }}
                >
                    <i>{svgIcon}</i>
                    {children}
                </div>
            </IconButton>
        )

        // Wrap tooltip target to preserve mouse event handling when button state changes
        if (enableTooltipOnDisabled) {
            return (
                <HoverTooltip text={<ToolTipText {...tooltip} />}>
                    <span>{iconButtonContent}</span>
                </HoverTooltip>
            )
        }

        // Original behavior: tooltip inside IconButton
        return (
            <IconButton
                data-testid={variant}
                disabled={disabledOrLoading}
                loading={loading}
                onClick={onClick}
                hasShadow={false}
                color="#ffffff"
                borderColor={iconBGColor}
                backgroundColor={iconBGColor}
                hoverColor="rgba(255, 255, 255, 0.2)"
                aria-label={`${tooltip?.label} button`}
            >
                <HoverTooltip text={<ToolTipText {...tooltip} />}>
                    <div
                        data-testid={`${variant}overlay`}
                        className={clsx(
                            iconOverlay,
                            hasMultipleIcons ? iconOverlayContainer : ''
                        )}
                        style={{ opacity: disabledOrLoading ? '0.45' : '1' }}
                    >
                        <i>{svgIcon}</i>
                        {children}
                    </div>
                </HoverTooltip>
            </IconButton>
        )
    }
)
